import { useEffect, useMemo, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { Header } from '@components/Header.tsx'
import { useGeolocation } from '@hooks/useGeolocation.ts'
import { useRouter } from '@router'
import type { Activity } from '@hewey/shared'
import { ActivityFilterBar } from './ActivityFilterBar.tsx'
import { ActivityViewToggle } from './ActivityViewToggle.tsx'
import { ACTIVITY_TYPE_META } from './activityLabels.ts'
import { matchesFilter, useActivityFilter } from './filter.ts'
import styles from './ActivityMapPage.module.css'

type Props = {
	activities: Activity[]
	loading: boolean
}

type PlaceActivity = Activity & {
	location: Extract<Activity['location'], { kind: 'place' }>
}

const isPlace = (a: Activity): a is PlaceActivity => a.location.kind === 'place'

// Empty-state view: roughly centred on the UK so the user knows they're
// looking at a map, not a broken canvas.
const FALLBACK_CENTER: [number, number] = [54, -2]
const FALLBACK_ZOOM = 5

export const ActivityMapPage = ({ activities, loading }: Props) => {
	const { navigate } = useRouter()
	const [filter, setFilter] = useActivityFilter()
	const { geo, retry } = useGeolocation(filter.maxDistanceMiles !== null)
	const origin = useMemo(
		() =>
			geo.status === 'granted' ? { lat: geo.lat, lng: geo.lng } : null,
		[geo],
	)

	const filtered = useMemo(
		() => activities.filter((a) => matchesFilter(a, filter, origin)),
		[activities, filter, origin],
	)
	const placePins = useMemo(() => filtered.filter(isPlace), [filtered])

	const containerRef = useRef<HTMLDivElement>(null)
	const mapRef = useRef<L.Map | null>(null)
	const markersRef = useRef<L.Marker[]>([])
	const navigateRef = useRef(navigate)
	navigateRef.current = navigate

	// One-shot map setup.
	useEffect(() => {
		if (!containerRef.current || mapRef.current) return
		const map = L.map(containerRef.current, {
			zoomControl: true,
			attributionControl: true,
		}).setView(FALLBACK_CENTER, FALLBACK_ZOOM)
		L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
			maxZoom: 19,
			attribution: '© OpenStreetMap contributors',
		}).addTo(map)
		mapRef.current = map
		return () => {
			map.remove()
			mapRef.current = null
		}
	}, [])

	// Re-render the markers when the filtered place set changes. We
	// clear the previous markers wholesale rather than diffing — the
	// counts are small enough (a household's worth) that it's not worth
	// the complexity.
	useEffect(() => {
		const map = mapRef.current
		if (!map) return

		for (const m of markersRef.current) m.remove()
		markersRef.current = []

		for (const a of placePins) {
			const { lat, lng } = a.location
			const marker = L.marker([lat, lng]).addTo(map)
			marker.bindPopup(
				buildPopup(a, () => navigateRef.current(`/activities/${a.id}`)),
			)
			markersRef.current.push(marker)
		}

		if (placePins.length === 0) {
			map.setView(FALLBACK_CENTER, FALLBACK_ZOOM)
		} else if (placePins.length === 1) {
			const only = placePins[0]
			if (only) map.setView([only.location.lat, only.location.lng], 14)
		} else {
			const bounds = L.latLngBounds(
				placePins.map(
					(a) => [a.location.lat, a.location.lng] as [number, number],
				),
			)
			map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 })
		}
	}, [placePins])

	const empty = !loading && activities.length > 0 && filtered.length === 0
	const noActivitiesAtAll = !loading && activities.length === 0

	return (
		<main className={styles.screen}>
			<Header right={<ActivityViewToggle />} />
			<ActivityFilterBar
				filter={filter}
				setFilter={setFilter}
				geo={geo}
				onRetryLocation={retry}
			/>

			<div className={styles.mapWrap}>
				<div ref={containerRef} className={styles.map} />

				{noActivitiesAtAll ? (
					<div className={styles.overlay}>
						Nothing in the bank yet.
					</div>
				) : empty ? (
					<div className={styles.overlay}>
						No activities match these filters.
					</div>
				) : null}
			</div>
		</main>
	)
}

// Build the popup DOM manually since Leaflet renders outside React's
// tree. We attach a native click handler that hands off to the SPA
// router rather than letting the anchor do a full page nav.
const buildPopup = (a: PlaceActivity, onOpen: () => void): HTMLElement => {
	const root = document.createElement('div')
	root.className = styles.popup ?? ''

	const title = document.createElement('strong')
	title.textContent = a.name
	root.appendChild(title)

	if (a.types.length > 0) {
		const sub = document.createElement('div')
		sub.textContent = a.types
			.map((t) => {
				const meta = ACTIVITY_TYPE_META[t]
				return `${meta.emoji} ${meta.label}`
			})
			.join(' · ')
		sub.className = styles.popupMeta ?? ''
		root.appendChild(sub)
	}

	const link = document.createElement('a')
	link.href = `/activities/${a.id}`
	link.textContent = 'Open →'
	link.className = styles.popupLink ?? ''
	link.addEventListener('click', (e) => {
		e.preventDefault()
		onOpen()
	})
	root.appendChild(link)

	return root
}
